import { DrawerFooter } from '@client/components/_core/layout/DrawerFooter';
import { OverlayPage } from '@client/components/_core/layout/OverlayPage';
import { PageController } from '@client/components/_core/layout/PageController';
import { TransactionTypeSelect } from '@client/components/_shared/selectors/financial/TransactionTypeSelect';
import { CurrencyInput } from '@client/components/_ui/data-entry/CurrencyInput';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import {
    heldMessage,
    ReserveChangePreviewError,
    ReserveChangePreviewLine,
    useReserveChangePreview,
} from '@client/components/claims/ReserveChangePreview';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useFormDirty } from '@client/hooks/forms/useFormDirty';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { trpc } from '@client/trpc/client';
import { buildCurrencyInputRules } from '@client/utils';
import { createFileRoute } from '@tanstack/react-router';
import { Form, Input } from 'antd';
import type { Decimal } from 'decimal.js';
import { useEffect, useId, useState } from 'react';
import { claimFormStore } from '../../../../store';

export const Route = createFileRoute(
    '/console/$orgId/claims/details/$id/financials/reserves/details/$reserveId/reserve-adjustments/add',
)({
    staticData: {
        presentation: 'overlay',
        metadata: {
            title: 'Add adjustment',
        },
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <ReserveAdjustmentEdit />
        </PageController>
    );
}

type Adjustment = {
    transactionTypeId: bigint;
    description: string;
    amount: Decimal;
};

const FormItem = Form.Item<Adjustment>;

function ReserveAdjustmentEdit() {
    const formId = useId();
    const isReadonly = claimFormStore.use.isReadonly();
    const claim = claimFormStore.use.formData();
    const { reserveId } = Route.useParams();
    const { trpcUtils } = useTrpc();
    const { mutate } = trpc.reserveAdjustment.createReserveAdjustment.useMutation();
    const navigate = useNavigate();

    const [form] = Form.useForm<Adjustment>();

    const formDirty = useFormDirty();
    // CUR-1641: an increase above the handler's limit is held for approval; a decrease never is.
    // The selected transaction type says which the adjustment is.
    const [nature, setNature] = useState<'RESERVE_INCREASE' | 'RESERVE_DECREASE'>();
    const amount = Form.useWatch(({ amount }) => amount, form);
    const {
        preview,
        error: previewError,
        retry,
        retrying,
        saveLabel,
        blocked,
    } = useReserveChangePreview({
        claimId: claim.id,
        kind: nature === undefined ? undefined : nature === 'RESERVE_DECREASE' ? 'DECREASE' : 'INCREASE',
        amount,
        // CUR-2676: a final payment's lock on this reserve, and the decrease floor, show before Save.
        reserveId,
    });

    useEffect(() => {
        if (isReadonly) {
            navigate({ moveInMatches: -1 });
        }
    }, [isReadonly]);

    if (isReadonly) {
        return null;
    }

    return (
        <OverlayPage
            isDirty={formDirty.isDirty}
            title="Add reserve adjustment"
            afterClose={() => {
                form.resetFields();
                navigate({ moveInMatches: -1 });
            }}
            footer={({ requestClose }) => (
                <DrawerFooter onCancel={requestClose} formId={formId} okText={saveLabel} okDisabled={blocked} />
            )}
        >
            {({ onClose }) => (
                <Form
                    form={form}
                    {...formDirty.formProps}
                    id={formId}
                    layout="vertical"
                    onFinish={(values) => {
                        message.loading('Saving...');

                        mutate(
                            {
                                description: values.description,
                                claimId: claim.id,
                                transactionTypeId: values.transactionTypeId,
                                reserveId,
                                amount: values.amount,
                            },
                            {
                                onSuccess(result) {
                                    if (result.held) {
                                        message.success(heldMessage(result.held));
                                        void trpcUtils.claimAuthority.getPendingReserveChange.invalidate();
                                    } else {
                                        message.success('Saved.');
                                        void trpcUtils.reserve.invalidate();
                                        void trpcUtils.reserveAdjustment.invalidate();
                                        void trpcUtils.claim.getFinancialSummary.invalidate();
                                    }
                                    onClose();
                                },
                                onError(error) {
                                    errorMessage.show(error);
                                },
                            },
                        );
                    }}
                >
                    {claim.productTypeId && (
                        <FormItem label="Transaction type" name="transactionTypeId" rules={[{ required: true }]}>
                            <TransactionTypeSelect
                                transactionNature={['RESERVE_INCREASE', 'RESERVE_DECREASE']}
                                productTypeId={claim.productTypeId}
                                onChange={(_, option) => {
                                    const selected = Array.isArray(option) ? option[0] : option;
                                    const selectedNature = selected?.data.transactionNature;
                                    setNature(
                                        selectedNature === 'RESERVE_INCREASE' || selectedNature === 'RESERVE_DECREASE'
                                            ? selectedNature
                                            : undefined,
                                    );
                                }}
                            />
                        </FormItem>
                    )}
                    <FormItem
                        label="Adjustment amount"
                        name="amount"
                        rules={buildCurrencyInputRules({ isRequired: true, gtZero: true })}
                    >
                        <CurrencyInput />
                    </FormItem>
                    <ReserveChangePreviewLine preview={preview} />
                    <ReserveChangePreviewError error={previewError} onRetry={retry} retrying={retrying} />
                    <FormItem label="Description" name="description" rules={[{ required: true }]}>
                        <Input.TextArea />
                    </FormItem>
                </Form>
            )}
        </OverlayPage>
    );
}
